import { type Dispatch, type SetStateAction, useState } from "react";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useOrderListFeedback } from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/components/OrderListFeedbackContext";
import { useTranslation } from "react-i18next";
import RequestFeedbackPopup from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/components/v2/RequestFeedbackPopup";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { type OrderData, type OrderLineItemData } from "~/domains/orders/types/modelsV2";
import type { CreateCaseSource } from "../../components/ServiceCases/types";

export const useSendOrderFeedback = (
  data: OrderLineItemData | undefined,
  orderData: OrderData | undefined,
  lineItemIndex: number,
  soNumber?: string,
  poNumber?: string,
  setShowSnackbar?: Dispatch<SetStateAction<{ open: boolean; message: string }>>,
  subLineNo?: string,
  source?: CreateCaseSource,
  onCaseCreated?: () => void,
) => {
  const [isConfirmationPopupOpen, setIsConfirmationPopupOpen] = useState<boolean>(false);
  const { setTrue: userHasCreatedCase, val: userCreateCase } = useBooleanState(false);
  const { t } = useTranslation();
  const openCloseConfirmationPopup = () => {
    close();
    setIsConfirmationPopupOpen((prev) => !prev);
    if (userHasPermission(Permission.CAN_SEE_SERVICE_CASES)) {
      userHasCreatedCase();
      setShowSnackbar?.({
        open: true,
        message: t("serviceCases.caseCreated.snackbarInfo"),
      });
      onCaseCreated?.();
    }
  };

  const { setTrue: open, setFalse: close, val: isModalOpen } = useBooleanState(false);

  const { userData, orderFeedbackOptions } = useOrderListFeedback();

  const popups = (
    <>
      {isModalOpen &&
        userData !== undefined &&
        orderFeedbackOptions !== undefined &&
        orderData !== undefined &&
        data !== undefined && (
          <RequestFeedbackPopup
            lineItemIndex={lineItemIndex}
            onClose={close}
            onSuccess={openCloseConfirmationPopup}
            userData={userData}
            soNumber={soNumber}
            poNumber={poNumber}
            orderFeedbackOptions={orderFeedbackOptions}
            orderDetailsEntryData={data}
            orderData={orderData}
            subLineNo={subLineNo}
            source={source}
          />
        )}
    </>
  );

  return [popups, open, userCreateCase] as const;
};
